import type { FC, MouseEvent, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { useRef } from 'react'

import { pad } from '@/lib/format'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'

import {
    DESKTOP,
    FULL_MOTION,
    gsap,
    useGSAP
} from '@/lib/gsap'

import { signatureHoles } from '@/constants/course'

interface SignatureHolesSectionProps {
    index?: string
}

export const SignatureHolesSection: FC<SignatureHolesSectionProps> = ({ index = '03' }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const pinRef = useRef<HTMLDivElement>(null)
    const scrollerRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const counterRef = useRef<HTMLSpanElement>(null)
    const progressRef = useRef<HTMLSpanElement>(null)

    const { scrollTo } = useSmoothScroll()

    useGSAP(() => {
        const section = sectionRef.current
        const track = trackRef.current
        const scroller = scrollerRef.current
        const counter = counterRef.current
        const progress = progressRef.current

        if (!section || !track || !scroller || !counter || !progress) return

        const cards = gsap.utils.toArray<HTMLElement>('[data-sig-card]')

        const showProgress = (value: number): void => {
            gsap.set(progress, { scaleX: value })
            const index = Math.min(cards.length - 1, Math.round(value * (cards.length - 1)))
            counter.textContent = pad(index + 1)
        }

        const mm = gsap.matchMedia()

        // Desktop: vertical scroll drives a pinned horizontal gallery.
        mm.add(`${FULL_MOTION} and ${DESKTOP}`, () => {
            const distance = (): number => Math.max(0, track.scrollWidth - window.innerWidth)

            const slide = gsap.to(track, {
                x: () => -distance(),
                ease: 'none',
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: () => `+=${distance()}`,
                    pin: pinRef.current,
                    scrub: 0.8,
                    invalidateOnRefresh: true,
                    onUpdate: (self) => showProgress(self.progress)
                }
            })

            cards.forEach((card) => {
                const image = card.querySelector('[data-sig-image]')
                const frame = card.querySelector('[data-sig-frame]')
                const details = card.querySelectorAll('[data-sig-detail]')
                const visibleAtStart = card.offsetLeft < window.innerWidth

                gsap.fromTo(image, { xPercent: -9 }, {
                    xPercent: 9,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: card,
                        containerAnimation: slide,
                        start: 'left right',
                        end: 'right left',
                        scrub: true
                    }
                })

                const reveal = gsap.timeline({
                    scrollTrigger: visibleAtStart
                        ? { trigger: section, start: 'top 65%', once: true }
                        : { trigger: card, containerAnimation: slide, start: 'left 85%', once: true }
                })

                reveal
                    .fromTo(frame, { clipPath: 'inset(0% 0% 0% 100%)' }, {
                        clipPath: 'inset(0% 0% 0% 0%)',
                        duration: 1.4,
                        ease: 'expo.inOut'
                    })
                    .from(details, {
                        yPercent: 40,
                        autoAlpha: 0,
                        duration: 1,
                        ease: 'expo.out',
                        stagger: 0.07
                    }, 0.5)
            })
        })

        // Phones, tablets and reduced motion: a native swipe gallery with the same progress bar.
        mm.add({ compact: '(max-width: 1023px)', reduce: '(prefers-reduced-motion: reduce)' }, (context) => {
            const { compact, reduce } = context.conditions as { compact: boolean, reduce: boolean }
            if (!compact && !reduce) return

            const onScroll = (): void => {
                const max = scroller.scrollWidth - scroller.clientWidth
                showProgress(max > 0 ? scroller.scrollLeft / max : 0)
            }

            onScroll()
            scroller.addEventListener('scroll', onScroll, { passive: true })
            return () => scroller.removeEventListener('scroll', onScroll)
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    const openMap = (event: MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault()
        scrollTo('#course-map')
    }

    return (
        <section
            ref={sectionRef}
            id='signature-holes'
            aria-labelledby='signature-heading'
            className='relative z-10 bg-charcoal text-bone'
        >
            <div ref={pinRef} className='relative flex flex-col lg:h-[100svh] lg:min-h-[640px] lg:overflow-hidden'>
                <div className='flex items-end justify-between gap-6 px-5 pt-20 sm:px-8 lg:px-12 lg:pt-24'>
                    <SectionLabel index={index} className='text-bone/70'>
                        Signature holes
                    </SectionLabel>

                    <p className='font-mono text-[11px] uppercase tracking-[0.24em] text-bone/60'>
                        <span ref={counterRef} className='text-bone'>
                            01
                        </span>
                        {' '}/ {pad(signatureHoles.length)}
                    </p>
                </div>

                <div
                    ref={scrollerRef}
                    className='no-scrollbar mt-10 snap-x snap-mandatory overflow-x-auto overscroll-x-contain lg:min-h-0 lg:flex-1 lg:motion-safe:snap-none lg:motion-safe:overflow-visible'
                >
                    <div ref={trackRef} className='flex w-max gap-5 px-5 pb-8 sm:gap-6 sm:px-8 lg:h-full lg:gap-8 lg:px-12 lg:pb-10'>
                        <div className='flex w-[82vw] shrink-0 snap-start flex-col justify-between gap-8 sm:w-[56vw] lg:w-[30vw] lg:pr-6'>
                            <div>
                                <SplitReveal
                                    as='h2'
                                    id='signature-heading'
                                    className='text-[clamp(2.1rem,3.6vw,3.6rem)] font-light leading-[1.04] tracking-[-0.03em]'
                                >
                                    Five holes you&apos;ll talk about <em className='font-serif font-normal'>on the drive home.</em>
                                </SplitReveal>

                                <p className='mt-6 max-w-sm text-[15px] leading-relaxed text-bone/70'>
                                    Each one was found on foot long before it was drawn. Walk them in a different light and they play like different holes.
                                </p>
                            </div>

                            <p className='hidden items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/60 lg:flex'>
                                Keep scrolling
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </p>
                        </div>

                        {signatureHoles.map((hole) => (
                            <article
                                key={hole.number}
                                data-sig-card
                                aria-label={`Hole ${hole.number}, ${hole.name}`}
                                className='flex w-[82vw] shrink-0 snap-start flex-col sm:w-[60vw] lg:h-full lg:w-[38vw]'
                            >
                                <div data-sig-frame className='relative aspect-[3/4] overflow-hidden rounded-[4px] bg-forest lg:aspect-auto lg:min-h-0 lg:flex-1'>
                                    <div data-sig-image className='absolute inset-y-0 -left-[12%] w-[124%]'>
                                        <Image
                                            src={hole.image.src}
                                            alt={hole.image.alt}
                                            fill
                                            sizes='(min-width: 1024px) 46vw, (min-width: 640px) 72vw, 98vw'
                                            className='object-cover'
                                            style={{ objectPosition: hole.image.position }}
                                        />
                                    </div>

                                    <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-pine/90 via-pine/10 to-pine/30' />

                                    <div className='absolute inset-x-0 top-0 flex items-start justify-between gap-4 p-4 font-mono text-[10px] uppercase tracking-[0.22em] sm:p-5 sm:text-[11px]'>
                                        <span className='rounded-full border border-bone/30 px-3 py-1.5 backdrop-blur-sm'>
                                            Hole {pad(hole.number)}
                                        </span>

                                        <span className='pt-1.5 text-bone/80'>
                                            Stroke index {hole.strokeIndex}
                                        </span>
                                    </div>

                                    <div className='absolute inset-x-0 bottom-0 p-4 sm:p-6'>
                                        <p data-sig-detail className='font-serif text-[clamp(4rem,7vw,7.5rem)] leading-[0.8]'>
                                            {pad(hole.number)}
                                        </p>

                                        <h3 data-sig-detail className='type-wide mt-3 text-[clamp(1.4rem,2.2vw,2.1rem)] font-medium uppercase leading-none'>
                                            {hole.name}
                                        </h3>

                                        <p data-sig-detail className='mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-bone/80'>
                                            Par {hole.par} · {hole.yards} yds
                                        </p>
                                    </div>
                                </div>

                                <div className='mt-5 grid gap-3 sm:grid-cols-[1fr_auto] sm:gap-6'>
                                    <p data-sig-detail className='max-w-md text-sm leading-relaxed text-bone/70'>
                                        {hole.description}
                                    </p>

                                    <p data-sig-detail className='font-mono text-[10px] uppercase tracking-[0.2em] text-ochre sm:max-w-[9rem] sm:text-right'>
                                        {hole.detail}
                                    </p>
                                </div>
                            </article>
                        ))}

                        <div className='flex w-[70vw] shrink-0 snap-start flex-col justify-end gap-6 sm:w-[40vw] lg:w-[24vw] lg:pb-4'>
                            <p className='font-serif text-[clamp(1.8rem,2.6vw,2.6rem)] leading-[1.1] text-bone/90'>
                                Thirteen more, and not one of them filler.
                            </p>

                            <a
                                href='#course-map'
                                onClick={openMap}
                                className='inline-flex h-12 w-fit items-center gap-3 rounded-full border border-bone/30 px-6 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors hover:border-bone hover:bg-bone hover:text-charcoal'
                            >
                                See the full routing
                                <ArrowUpRight className='size-4' aria-hidden='true' />
                            </a>
                        </div>
                    </div>
                </div>

                <div aria-hidden='true' className='mx-5 mb-10 h-px bg-bone/15 sm:mx-8 lg:mx-12 lg:mb-10'>
                    <span ref={progressRef} className='block h-full origin-left scale-x-0 bg-ochre' />
                </div>
            </div>
        </section>
    )
}
